import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { BackButton } from "@/components/common/BackButton";
import { Button } from "@/components/design/Button";
import {
  CreateDocumentGroupDocument,
  DeleteCurriculumVitaeDocument,
  DeleteDocumentDocument,
  DeleteDocumentGroupDocument,
  DocumentContentDocument,
  MyCurriculumVitaesDocument,
  UpdateCurriculumVitaeDocument,
  UpdateDocumentDocument,
  UpdateDocumentGroupDocument,
} from "@/graphql/generated/operations";
import { CV_API_VERSION } from "@/lib/cv-model";
import {
  graphQlErrorExtensions,
  throwForValidationResult,
  userFacingGraphqlError,
} from "@/lib/graphql-error";
import { base64ToBlob, useOpenDocument } from "@/lib/use-open-document";
import { takePendingUpload } from "@/lib/pending-upload";
import { firstRenameCollision, folderNameTaken } from "@/pages/save/service/duplicate-guards";
import { useMe } from "@/auth/use-me";
import { DocumentDeleteConflictAlert } from "./service/DocumentDeleteConflictAlert";
import { FolderDeleteReport, type KeptDocument } from "./service/FolderDeleteReport";
import { useServiceActions } from "./service/use-service-actions";
import { PAGE_FETCH, toDocumentItem, toExtractionDoc, useMyDocuments, useUploadMyDocuments } from "./service/use-my-documents";
import { DOCUMENT_USED_IN_SHARED_ITEMS } from "@/lib/genesis-error-codes";
import { LocalizedError } from "@/lib/localized-error";
import { DocumentLibrary } from "@/components/documents/DocumentLibrary";
import type { DocumentItem } from "@/components/documents/types";

// Save · Service - node-by-node port of workshop's screens/Save.jsx
// ServiceTab. Two-column action zone (Input / Output) feeding into a
// full-width Documents box with drag-and-drop upload + filter.
//
// Workshop's INPUT cards (Power-Analyse, CV-Extractor) are static
// design-spec previews - no backend. The OUTPUT CV-Generator opens the
// CvEditor; saving persists a re-editable CurriculumVitae draft that is
// listed alongside uploaded Documents below. PDFs are download-only
// (via the editor's previewer), mirroring discovery's CV model.
//
// Documents grid + upload uses tech-stack's existing MyDocuments +
// CreateDocument ops.


// The Documents box loads BOTH its connections (uploaded documents + CV drafts) in full, the documents
// through useMyDocuments, the CV drafts here with the same PAGE_FETCH walk; the fully-loaded loose/Unsorted
// list then shows DOCS_PER_PAGE at a time client-side.

type SharedItemsConflict = { kind: "document-used-in-shared-items"; sharedItemIds: string[] };

type ServiceError = string | SharedItemsConflict;

type FolderError = ServiceError | { kind: "folder-documents-kept"; deleted: number; kept: KeptDocument[] };

function documentDeleteConflict(error: unknown): SharedItemsConflict | null {
  const extensions = graphQlErrorExtensions(error, DOCUMENT_USED_IN_SHARED_ITEMS);
  const sharedItemIds = extensions?.["sharedItemIds"];
  if (!Array.isArray(sharedItemIds)) return null;

  const ids = [...new Set(sharedItemIds.filter((id): id is string => typeof id === "string"))];
  return ids.length > 0
    ? { kind: "document-used-in-shared-items", sharedItemIds: ids }
    : null;
}

export function ServicePage() {
  const intl = useIntl();
  const {
    docs,
    groups: docGroups,
    loading,
    draining: docsDraining,
    error,
    paginationError: docsPaginationError,
    retryPagination: retryDocsPagination,
    refetch: docsRefetch,
    groupsRefetch,
  } = useMyDocuments();
  const {
    data: cvData,
    loading: cvsLoading,
    error: cvsError,
    refetch: cvsRefetch,
    fetchMore: cvsFetchMore,
  } = useQuery(MyCurriculumVitaesDocument, { variables: { input: { first: PAGE_FETCH } } });
  const cvNodes = cvData?.curriculumVitaes?.nodes;
  const cvs = useMemo(() => cvNodes ?? [], [cvNodes]);

  // Every stored item name, for the duplicate pre-checks below. genesis has no unique constraint on
  // document_name or curriculum_vitae.name, so nothing downstream catches what these miss. Scoped
  // per person rather than per folder, matching how ExtractionDialog already compares upload names.
  // Shared by the pre-checks and by the server-error paths, so the two report a collision identically.
  const duplicateNameMsg = intl.formatMessage({
    id: "common.duplicateName",
    defaultMessage: "An entry with this name already exists. Please choose a different name.",
  });

  const storedNames = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => [...docs, ...cvs].map((d: any) => ({ id: d.id as string, name: (d.name ?? "") as string })),
    [docs, cvs],
  );

  // Drain every remaining page of both connections so the folder accordion and
  // the document tally see the COMPLETE set. Each appended page (merged in
  // graphql/client.ts) advances pageInfo, which re-runs the effect until the
  // connection reports no further pages.
  const [cvsPaginationError, setCvsPaginationError] = useState<string | null>(null);
  const cvsPageInfo = cvData?.curriculumVitaes?.pageInfo;
  useEffect(() => {
    if (!cvsPaginationError && cvsPageInfo?.hasNextPage && cvsPageInfo.endCursor) {
      void cvsFetchMore({
        variables: { input: { first: PAGE_FETCH, after: cvsPageInfo.endCursor } },
      }).catch(failure => setCvsPaginationError(userErrorMessage(intl, failure)));
    }
  }, [cvsPageInfo, cvsFetchMore, intl, cvsPaginationError]);
  const paginationError = docsPaginationError ?? cvsPaginationError;
  const listLoading = loading || cvsLoading || docsDraining || Boolean(cvsPageInfo?.hasNextPage);
  const [updateDocument] = useMutation(UpdateDocumentDocument);
  const [deleteDocument] = useMutation(DeleteDocumentDocument);
  const [updateCv] = useMutation(UpdateCurriculumVitaeDocument);
  const [deleteCvMutation] = useMutation(DeleteCurriculumVitaeDocument);
  const [createGroup] = useMutation(CreateDocumentGroupDocument);
  const [updateGroup] = useMutation(UpdateDocumentGroupDocument);
  const [deleteGroup] = useMutation(DeleteDocumentGroupDocument);
  const apollo = useApolloClient();
  const me = useMe();
  const [libraryFilters, setLibraryFilters] = useState({ query: "", fileType: "all" });
  const docQuery = libraryFilters.query;
  const fileTypeFilter = libraryFilters.fileType;
  const [uploadError, setUploadError] = useState<ServiceError | null>(null);
  const { upload, busy: uploadBusy } = useUploadMyDocuments({ storedNames, onError: setUploadError });

  // Folder (document-group) errors show in the folder pane, next to "New folder"
  const [folderError, setFolderError] = useState<FolderError | null>(null);
  const openDocument = useOpenDocument(setUploadError);
  // Input/Output actions (Power/Skill analysis, LinkedIn import, CV generator,
  // data export) plus their dialogs live in a shared hook, so the profile
  // ServiceMenu drives the exact same flows. See use-service-actions.
  const extractionDocs = useMemo(() => docs.map(toExtractionDoc), [docs]);
  const { openCv, dialogs: serviceDialogs } = useServiceActions({
    docs: extractionDocs,
    groups: docGroups,
    onChanged: () => {
      void docsRefetch();
      void cvsRefetch();
    },
    onError: setUploadError,
  });

  async function handleUpload(files: FileList | File[] | null, groupId: string | null = null) {
    if ((await upload(files, groupId)).length) await docsRefetch();
  }

  // Files handed off from the home "+ Datei hochladen" quick action. Taken on mount, so
  // leaving before the upload drops them rather than uploading them on a later visit.
  // Uploaded once me.id is available (handleUpload needs the owner) AND the list loaded
  // without error: the duplicate-name guard checks against the stored names, and an
  // upload that finished during the first load stayed out of the list until a reload.
  const handedOffRef = useRef<File[] | null>(null);
  useEffect(() => {
    handedOffRef.current ??= takePendingUpload();
  }, []);
  useEffect(() => {
    const pending = handedOffRef.current;
    if (!pending?.length || !me.id || listLoading || error || cvsError || paginationError) return;
    handedOffRef.current = null;
    // Defer so the upload's setState runs after this effect, not within it.
    queueMicrotask(() => void handleUpload(pending));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.id, listLoading, error, cvsError, paginationError]);

  async function handleRenameDoc(id: string, newName: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const orig = docs.find((d: any) => d.id === id);
    const tags = (orig?.tags as string[] | null) ?? [];
    setUploadError(null);
    try {
      const res = await updateDocument({
        // Re-send tags so the rename doesn't wipe the document's tags
        // (e.g. the "Lebenslauf" CV marker).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variables: { id, input: { data: { name: newName, tags }, dryRun: false } } as any,
      });
      const out = res.data?.updateDocument;
      if (out?.__typename !== "UpdateDocumentResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errRename", defaultMessage: "Rename failed." }));
      }
      await docsRefetch();
    } catch (e) {
      const message = userFacingGraphqlError(intl, e, duplicateNameMsg);
      setUploadError(message);
      throw new LocalizedError(message, { cause: e });
    }
  }

  async function handleDeleteDoc(id: string) {
    setUploadError(null);
    try {
      await deleteDocument({ variables: { input: { id, dryRun: false } } });
      await docsRefetch();
      return true;
    } catch (e) {
      setUploadError(documentDeleteConflict(e) ?? userFacingGraphqlError(intl, e));
      return false;
    }
  }

  async function handleOpenDoc(id: string) {
    setUploadError(null);
    await openDocument(id);
  }

  // CV drafts are CurriculumVitae records, so rename/delete go through the
  // CV mutations - the document mutations don't apply to them.
  async function handleRenameCv(id: string, newName: string) {
    setUploadError(null);
    try {
      const res = await updateCv({
        variables: {
          id,
          input: { data: { apiVersion: CV_API_VERSION, name: newName }, dryRun: false },
        },
      });
      const out = res.data?.updateCurriculumVitae;
      if (out?.__typename !== "UpdateCurriculumVitaeResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errRename", defaultMessage: "Rename failed." }));
      }
      await cvsRefetch();
    } catch (e) {
      const message = userFacingGraphqlError(intl, e, duplicateNameMsg);
      setUploadError(message);
      throw new LocalizedError(message, { cause: e });
    }
  }

  async function handleDeleteCv(id: string) {
    setUploadError(null);
    try {
      await deleteCvMutation({ variables: { input: { id, dryRun: false } } });
      await cvsRefetch();
      return true;
    } catch (e) {
      setUploadError(userFacingGraphqlError(intl, e));
      return false;
    }
  }

  async function handleDownloadDoc(id: string) {
    setUploadError(null);
    try {
      const { data: docData } = await apollo.query({
        query: DocumentContentDocument,
        variables: { id },
        fetchPolicy: "network-only",
      });
      const doc = docData?.document;
      const file = doc?.content;
      if (!file?.content) throw new LocalizedError(intl.formatMessage({ id: "service.page.errNoContent", defaultMessage: "No content available." }));
      const blob = base64ToBlob(file.content, file.mimeType ?? "application/octet-stream");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc?.fileName ?? "dokument";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setUploadError(userFacingGraphqlError(intl, e));
    }
  }

  // Folder (DocumentGroup) management. Creating/renaming/deleting folders is
  // distinct from moving documents into them (bulkMove above). Deleting a
  // folder orphans its documents server-side (genesis nulls DOCUMENT_GROUP_ID),
  // so they simply reappear without a folder in the overall list.
  // Returns success — the New-folder field stays open on failure to show its error.
  async function handleCreateGroup(name: string): Promise<boolean> {
    const trimmed = name.trim();
    if (!trimmed) return false;
    setFolderError(null);
    // Nothing on the server stops this: document_group has no unique constraint on name, so two
    // identically named folders could be created and never told apart again. Scope is the person's
    // own folder list, which docGroups already holds in full.
    if (folderNameTaken(docGroups, trimmed)) {
      setFolderError(duplicateNameMsg);
      return false;
    }
    try {
      const res = await createGroup({
        variables: { input: { person: me.id ?? undefined, data: { name: trimmed }, dryRun: false } },
      });
      const out = res.data?.createDocumentGroup;
      if (out?.__typename === "TooManyRecordsResult") {
        setFolderError(
          intl.formatMessage(
            { id: "service.page.errFolderLimit", defaultMessage: "You have reached the maximum of {limit} folders." },
            { limit: out.limit ?? "" },
          ),
        );
        return false;
      }
      if (out?.__typename !== "CreateDocumentGroupResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errFolderCreate", defaultMessage: "Folder could not be created." }));
      }
      await groupsRefetch();
      return true;
    } catch (e) {
      setFolderError(userFacingGraphqlError(intl, e, duplicateNameMsg));
      return false;
    }
  }

  async function handleRenameGroup(id: string, name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setFolderError(null);
    // FolderRow's inline edit only guards "unchanged", not "collides with a sibling". Excluding this
    // folder's own current name keeps a no-op rename legal.
    if (folderNameTaken(docGroups, trimmed, id)) {
      setFolderError(duplicateNameMsg);
      return;
    }
    try {
      const res = await updateGroup({
        variables: { id, input: { data: { name: trimmed }, dryRun: false } },
      });
      const out = res.data?.updateDocumentGroup;
      if (out?.__typename !== "UpdateDocumentGroupResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errFolderRename", defaultMessage: "Folder could not be renamed." }));
      }
      await groupsRefetch();
    } catch (e) {
      setFolderError(userFacingGraphqlError(intl, e, duplicateNameMsg));
    }
  }

  // mode "keep": only the folder goes - genesis orphans its documents, so
  // they fall back into "Unsortiert". mode "withFiles": every document that
  // can go goes, as in a file manager; one genesis refuses stays, and with it
  // the folder, and the report names each kept document and why (spark#1028).
  async function handleDeleteGroup(id: string, mode: "keep" | "withFiles") {
    setFolderError(null);
    if (mode === "withFiles") {
      const { deleted, kept } = await deleteDocumentsOfGroup(id);
      if (kept.length > 0) {
        setFolderError({ kind: "folder-documents-kept", deleted, kept });
        await Promise.all([groupsRefetch(), docsRefetch()]);
        return;
      }
    }
    try {
      await deleteGroup({ variables: { input: { id, dryRun: false } } });
      // Refetch docs too: their group reference was orphaned server-side.
      await Promise.all([groupsRefetch(), docsRefetch()]);

    } catch (e) {
      setFolderError(documentDeleteConflict(e) ?? userFacingGraphqlError(intl, e));
    }
  }

  // One refused document must not stop the others: each is tried, each refusal is collected.
  async function deleteDocumentsOfGroup(id: string): Promise<{ deleted: number; kept: KeptDocument[] }> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inGroup = docs.filter((d: any) => (d.group?.id ?? null) === id);
    const kept: KeptDocument[] = [];
    for (const d of inGroup) {
      try {
        await deleteDocument({ variables: { input: { id: d.id, dryRun: false } } });
      } catch (e) {
        kept.push({
          id: d.id,
          name: toDocumentItem(d, intl).name,
          reason: documentDeleteConflict(e) ?? userFacingGraphqlError(intl, e),
        });
      }
    }
    return { deleted: inGroup.length - kept.length, kept };
  }

  // Move a single document into a folder (groupId) or out of any folder
  // (null). Drag & drop target; CV drafts have no group and aren't movable.
  async function moveDocToGroup(id: string, groupId: string | null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const orig = docs.find((d: any) => d.id === id);
    if (!orig) return false;
    if ((orig.group?.id ?? null) === groupId) return true; // already there
    try {
      const tags = (orig.tags as string[] | null) ?? [];
      const res = await updateDocument({
        // Re-send tags so the move doesn't wipe them (see rename).
        variables: {
          id,
          input: { data: { tags, group: groupId ? { id: groupId } : null }, dryRun: false },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
      const out = res.data?.updateDocument;
      if (out?.__typename !== "UpdateDocumentResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errMove", defaultMessage: "Move failed." }));
      }
      await docsRefetch();
      return true;
    } catch (e) {
      setUploadError(userFacingGraphqlError(intl, e));
      return false;
    }
  }

  const allItems = useMemo<DocumentItem[]>(() => {
    const docItems = docs.map((d) => toDocumentItem(d, intl));
    const cvItems = cvs.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (c: any): DocumentItem => ({
        id: c.id,
        canMove: false,
        canDownload: false,
        cvGenerator: true,
        kind: "CV",
        name: (c.name as string | null) ?? intl.formatMessage({ id: "service.page.cvFallbackName", defaultMessage: "CV" }),
        when: (c.createdAt as string | null) ?? null,
        source: intl.formatMessage({ id: "service.page.sourceCvGenerator", defaultMessage: "CV generator" }),
        fileType: "CV",
        groupId: null,
        preview: null,
      }),
    );
    return [...docItems, ...cvItems].sort((a, b) =>
      String(b.when ?? "").localeCompare(String(a.when ?? "")),
    );
  }, [docs, cvs, intl]);

  // Guided tour (/tour): summary + upload, list and filter regions with live counts.
  useAssistantTour({
    label: intl.formatMessage({
      id: "service.tour.label",
      defaultMessage: "Save Documents",
    }),
    summary: intl.formatMessage(
      {
        id: "service.tour.summary",
        defaultMessage:
          "This is your documents page: {count} documents in {folders} folders, from manual uploads to generated CVs.",
      },
      { count: allItems.length, folders: docGroups.length },
    ),
    stops: [
      {
        anchor: "save.documents.upload",
        title: intl.formatMessage({ id: "service.tour.upload.title", defaultMessage: "Upload files" }),
        description: intl.formatMessage({
          id: "service.tour.upload.desc",
          defaultMessage:
            "Upload files here (max. 10 MB each), or simply drag and drop them onto a folder or the list below.",
        }),
      },
      {
        anchor: "save.documents.list",
        title: intl.formatMessage({ id: "service.tour.list.title", defaultMessage: "Your documents" }),
        description: intl.formatMessage(
          {
            id: "service.tour.list.desc",
            defaultMessage:
              "{count} documents in {folders} folders. Open a folder to see its files, drag documents between folders, and hover a row to rename, select or delete it.",
          },
          { count: allItems.length, folders: docGroups.length },
        ),
      },
      {
        anchor: "save.documents.filters",
        title: intl.formatMessage({ id: "service.tour.filters.title", defaultMessage: "Search & filter" }),
        description: intl.formatMessage({
          id: "service.tour.filters.desc",
          defaultMessage:
            "Filter your documents by name, source or file type, and switch between preview and compact view.",
        }),
      },
    ],
  });

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the user's own document storage.
  useAssistantContext([
    {
      description: "the user's documents page",
      value:
        `${allItems.length} documents in ${docGroups.length} folders` +
        (docQuery ? `; search "${docQuery}"` : "") +
        (fileTypeFilter !== "all" ? `; filtered to type ${fileTypeFilter}` : ""),
    },
  ]);


  return (
    // Generous bottom padding so the Documents box doesn't pin the working
    // area to the bottom edge of the viewport - you scroll into free space.
    <div
      className="mt-8 max-w-[1600px] space-y-5 px-4 pb-40 @sm:px-6 @lg:px-10"
      data-graphql="MyDocuments, MyCurriculumVitaes, MyDocumentGroups, CreateDocument, UpdateDocument, DeleteDocument, UpdateCurriculumVitae, DeleteCurriculumVitae, CreateDocumentGroup, UpdateDocumentGroup, DeleteDocumentGroup"
    >
      <div>
        <BackButton />
      </div>

      <DocumentLibrary
        scopeId="service-documents" idPrefix="documents" items={allItems} titleVariant="hero"
        description={<FormattedMessage id="service.page.documentsHint" />}
        loading={listLoading}
        busy={uploadBusy} onUpload={handleUpload}
        onOpen={id => cvs.some(cv => cv.id === id) ? openCv(id) : handleOpenDoc(id)}
        onDownload={handleDownloadDoc}
        onRename={async (id, name) => {
          if (cvs.some(cv => cv.id === id)) await handleRenameCv(id, name);
          else await handleRenameDoc(id, name);
        }}
        validateRenames={changes => {
          if (firstRenameCollision(changes, storedNames)) throw new LocalizedError(duplicateNameMsg);
        }}
        onDelete={async ids => {
          const failed: string[] = [];
          for (const id of ids) {
            const ok = cvs.some(cv => cv.id === id) ? await handleDeleteCv(id) : await handleDeleteDoc(id);
            if (!ok) failed.push(id);
          }
          return failed;
        }}
        folders={{ items: docGroups, onCreate: handleCreateGroup, onRename: handleRenameGroup,
          onDelete: handleDeleteGroup, onMove: async (ids, groupId) => {
            setUploadError(null);
            const failed: string[] = [];
            for (const id of ids) if (!await moveDocToGroup(id, groupId)) failed.push(id);
            return failed;
          },
          error: typeof folderError === "string" ? folderError
            : folderError?.kind === "document-used-in-shared-items" ? <DocumentDeleteConflictAlert id="documents-newfolder-error" sharedItemIds={folderError.sharedItemIds} />
            : folderError?.kind === "folder-documents-kept" ? <FolderDeleteReport id="documents-newfolder-error" deleted={folderError.deleted} kept={folderError.kept} /> : null,
        }}
        error={typeof uploadError === "string" ? <div id="documents-error" data-testid="service-error" className="mt-3 smycles-field-meta text-ink">{uploadError}</div>
          : uploadError?.kind === "document-used-in-shared-items" ? <DocumentDeleteConflictAlert id="documents-error" sharedItemIds={uploadError.sharedItemIds} />
          : paginationError || error || cvsError ? <>
            <p role="alert"><FormattedMessage id="service.page.error" values={{ message: paginationError ?? userErrorMessage(intl, error ?? cvsError) }} /></p>
            {paginationError && <Button variant="secondary" size="sm" className="mt-2" onClick={() => { retryDocsPagination(); setCvsPaginationError(null); }}>
              <FormattedMessage id="common.tryAgain" defaultMessage="Try again" />
            </Button>}
          </> : null}
        onFilterChange={setLibraryFilters} assistantAnchorPrefix="save.documents"
      />

      {serviceDialogs}
    </div>
  );
}
